import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Mail, Copy, Send, Check } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * Universal "send invite email" dialog.
 * Props:
 *  - open / onOpenChange
 *  - targetRole: 'worker' (client invites a worker) | 'client' (worker invites a client)
 *  - onSent(result)        — called once the invite has been issued so the parent can refresh
 *  - testIdPrefix          — defaults to 'invite'
 */
export default function InviteDialog({ open, onOpenChange, targetRole, onSent, testIdPrefix = "invite" }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);   // { sent, link, ... }
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) { setEmail(""); setName(""); setMessage(""); setResult(null); setCopied(false); }
  }, [open]);

  const isClientInvitingWorker = targetRole === "worker";
  const title = isClientInvitingWorker ? "Invite a support worker" : "Invite a client";
  const sub = isClientInvitingWorker
    ? "Send a friendly email with a link to set up their support-worker account. The link expires in 14 days."
    : "Send a friendly email with a link inviting your NDIS participant to join the hub. Once they accept, they'll see you in their workers list automatically.";

  const send = async () => {
    if (!email.trim()) { toast.error("Email required"); return; }
    setBusy(true); setResult(null);
    try {
      const res = await api.post("/auth/invite", {
        email: email.trim().toLowerCase(),
        target_role: targetRole,
        full_name: name.trim(),
        message: message.trim(),
      });
      setResult(res.data);
      if (res.data.sent) toast.success("Invite email sent");
      else toast("Invite link created. Email sending isn't configured — copy & share manually.", { icon: "✉️" });
      onSent?.(res.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(result.link);
      setCopied(true); toast.success("Link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error("Couldn't copy — select and copy manually."); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl max-w-md" data-testid={`${testIdPrefix}-dialog`}>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2"><Mail className="w-5 h-5" /> {title}</DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">{sub}</DialogDescription>
        </DialogHeader>

        {!result ? (
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase font-bold tracking-wider">Their email</Label>
              <Input type="email" placeholder="name@example.com" value={email}
                     onChange={(e) => setEmail(e.target.value)}
                     data-testid={`${testIdPrefix}-email-input`}
                     className="rounded-xl min-h-[48px]" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase font-bold tracking-wider">Their name (optional)</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)}
                     data-testid={`${testIdPrefix}-name-input`}
                     className="rounded-xl min-h-[48px]"
                     placeholder={isClientInvitingWorker ? "e.g. Jamie Singh" : "e.g. Sarah Thompson"} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase font-bold tracking-wider">Personal message (optional)</Label>
              <Textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)}
                        data-testid={`${testIdPrefix}-message-input`}
                        className="rounded-xl"
                        placeholder={isClientInvitingWorker ? "Hi! I'd love you to use my NDIS app for shift logging." : "Hi! Joining will let us share rosters & timesheets in one place."} />
            </div>
          </div>
        ) : (
          <div className="space-y-3" data-testid={`${testIdPrefix}-result`}>
            <div className={`rounded-2xl border-2 p-4 flex items-center gap-3 ${result.sent ? "bg-emerald-500/10 border-emerald-500/30" : "bg-amber-500/10 border-amber-500/30"}`}>
              {result.sent ? <Check className="w-6 h-6 text-emerald-600" /> : <Mail className="w-6 h-6 text-amber-600" />}
              <div>
                <div className="font-bold text-sm">
                  {result.sent ? `Email sent to ${email}` : "Invite link created"}
                </div>
                <div className="text-xs text-muted-foreground">
                  {result.sent ? "They'll receive a sign-up link valid for 14 days." :
                   "Your SMTP isn't configured yet — share the link manually below."}
                </div>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase font-bold tracking-wider">Invite link</Label>
              <div className="flex gap-2">
                <Input value={result.link} readOnly
                       data-testid={`${testIdPrefix}-link-input`}
                       className="rounded-xl font-mono text-xs min-h-[44px] flex-1" />
                <Button type="button" onClick={copyLink} variant="outline"
                        data-testid={`${testIdPrefix}-copy-btn`}
                        className="rounded-full font-bold gap-1.5">
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />} {copied ? "Copied" : "Copy"}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                You can paste this link into a text, WhatsApp or another email and the recipient will land on the sign-up form pre-filled.
              </p>
            </div>
          </div>
        )}

        <DialogFooter>
          {!result ? (
            <Button onClick={send} disabled={busy || !email.trim()}
                    data-testid={`${testIdPrefix}-send-btn`}
                    className="rounded-full font-bold gap-2"
                    style={{ background: "hsl(var(--client))" }}>
              <Send className="w-4 h-4" /> {busy ? "Sending…" : "Send invite"}
            </Button>
          ) : (
            <Button onClick={() => onOpenChange?.(false)} className="rounded-full font-bold"
                    data-testid={`${testIdPrefix}-done-btn`}
                    style={{ background: "hsl(var(--client))" }}>
              Done
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
